import { useEffect, useId, useMemo, useRef } from 'react';
import './StudibudiFace.css';

interface StudibudiFaceProps {
  className?: string;
  isActive?: boolean;
}

const StudibudiFace = ({ className = '', isActive = false }: StudibudiFaceProps) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const uniqueId = useId().replace(/:/g, '');
  const blinkDelay = useMemo(() => `${(Math.random() * 3 + 1).toFixed(2)}s`, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.style.setProperty('--blink-delay', blinkDelay);
  }, [blinkDelay]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const reset = () => {
      el.style.setProperty('--pupil-x', '0px');
      el.style.setProperty('--pupil-y', '0px');
      el.style.setProperty('--head-x', '0px');
      el.style.setProperty('--head-y', '0px');
      el.style.setProperty('--head-rotate', '0deg');
      el.style.setProperty('--brow-y', '0px');
      el.style.setProperty('--brow-rotate', '0deg');
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      reset();
      return;
    }

    let raf = 0;
    const handlePointerMove = (event: PointerEvent) => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const x = event.clientX - (rect.left + rect.width / 2);
        const y = event.clientY - (rect.top + rect.height / 2);
        const clamp = (value: number) => Math.max(-1, Math.min(1, value));
        const normX = clamp(x / (rect.width / 2));
        const normY = clamp(y / (rect.height / 2));

        // Increased pupil movement for more expressive tracking
        const pupilX = normX * 5;
        const pupilY = normY * 4;
        // Reduced head movement to emphasize eye tracking
        const headX = normX * 0.8;
        const headY = normY * 0.5;
        const headRotate = normX * 1.2;
        // Increased brow curve for expressiveness
        const browY = normY * 1.2;
        const browRotate = normX * 2.5;

        el.style.setProperty('--pupil-x', `${pupilX.toFixed(2)}px`);
        el.style.setProperty('--pupil-y', `${pupilY.toFixed(2)}px`);
        el.style.setProperty('--head-x', `${headX.toFixed(2)}px`);
        el.style.setProperty('--head-y', `${headY.toFixed(2)}px`);
        el.style.setProperty('--head-rotate', `${headRotate.toFixed(2)}deg`);
        el.style.setProperty('--brow-y', `${browY.toFixed(2)}px`);
        el.style.setProperty('--brow-rotate', `${browRotate.toFixed(2)}deg`);
      });
    };

    // Always enable cursor tracking (not just when active)
    window.addEventListener('pointermove', handlePointerMove);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      if (raf) window.cancelAnimationFrame(raf);
      reset();
    };
  }, []); // Removed isActive dependency - always track

  const leftClipId = `sb-eye-left-${uniqueId}`;
  const rightClipId = `sb-eye-right-${uniqueId}`;

  return (
    <span
      ref={wrapperRef}
      className={`studibudi-face ${isActive ? 'active' : ''} ${className}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 132 174" fill="none" xmlns="http://www.w3.org/2000/svg" className="sb-face-svg">
        <defs>
          <clipPath id={leftClipId}>
            <circle cx="15.66" cy="61.1952" r="15.66" />
          </clipPath>
          <clipPath id={rightClipId}>
            <circle cx="115.35" cy="61.1952" r="15.66" />
          </clipPath>
        </defs>
        <g className="sb-face-bob">
          <g className="sb-face">
            <path
              d="M111.141 0C113.465 0.000254712 115.35 1.90903 115.351 4.26367V36.5352C101.731 36.5352 90.6904 47.576 90.6904 61.1953C90.6905 74.6018 101.389 85.509 114.714 85.8467L115.351 85.8555V169.65C115.35 172.005 113.465 173.914 111.141 173.914H19.8701C17.5451 173.914 15.6604 172.005 15.6602 169.65V85.8555C29.2794 85.8554 40.3202 74.8145 40.3203 61.1953C40.3203 47.576 29.2794 36.5352 15.6602 36.5352V4.26367C15.6604 1.90889 17.5451 2.14018e-05 19.8701 0H111.141Z"
              fill="#00CFAA"
              className="sb-head"
            />
            <g className="sb-eye sb-eye-right">
              <path
                d="M115.35 76.8552C123.999 76.8552 131.01 69.8439 131.01 61.1952C131.01 52.5464 123.999 45.5352 115.35 45.5352C106.702 45.5352 99.6904 52.5464 99.6904 61.1952C99.6904 69.8439 106.702 76.8552 115.35 76.8552Z"
                fill="#212121"
                className="sb-eye-shell"
              />
              <g className="sb-pupil-track" clipPath={`url(#${rightClipId})`}>
                <circle cx="115.35" cy="61.1952" r="5.4" fill="#0B0B0B" className="sb-pupil" />
                <circle cx="112.2" cy="58.4" r="1.6" fill="#F8F8F6" className="sb-eye-shine" />
              </g>
            </g>
            <g className="sb-eye sb-eye-left">
              <path
                d="M15.66 76.8552C24.3088 76.8552 31.32 69.8439 31.32 61.1952C31.32 52.5464 24.3088 45.5352 15.66 45.5352C7.01122 45.5352 0 52.5464 0 61.1952C0 69.8439 7.01122 76.8552 15.66 76.8552Z"
                fill="#212121"
                className="sb-eye-shell"
              />
              <g className="sb-pupil-track" clipPath={`url(#${leftClipId})`}>
                <circle cx="15.66" cy="61.1952" r="5.4" fill="#0B0B0B" className="sb-pupil" />
                <circle cx="12.6" cy="58.4" r="1.6" fill="#F8F8F6" className="sb-eye-shine" />
              </g>
            </g>
            <path
              d="M57.8696 63.8608L39.1033 109.86C36.5196 116.181 40.8859 123.176 47.7003 123.633L55.8101 124.174"
              stroke="#212121"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="sb-nose"
            />
            <path
              d="M90.1432 23.045C101.286 12.2221 110.572 17.2769 119.177 24.8347"
              stroke="#212121"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="sb-brow sb-brow-right"
            />
            <path
              d="M94.7353 146.048C88.1536 166.331 62.238 165.743 36.099 145.473"
              stroke="#212121"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="sb-mouth"
            />
            <path
              d="M11.2466 25.3866C21.3306 13.5927 31.0475 17.7468 40.3201 24.4512"
              stroke="#212121"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="sb-brow sb-brow-left"
            />
          </g>
        </g>
      </svg>
    </span>
  );
};

export default StudibudiFace;
